import { Menu } from '@base-ui/react/menu'
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { type ComponentType, useEffect, useState } from 'react'
import {
  CompassIcon,
  GlobeIcon,
  HomeIcon,
  LibraryIcon,
  LogoutIcon,
  SearchIcon,
  UserIcon,
} from '@/components/ui/icons'
import { EarMark } from '@/components/ui/primitives'
import { CommandPalette } from '@/features/search/command-palette'
import { LOCALE_LABELS, LOCALES } from '@/i18n/locales'
import type { MessageKey } from '@/i18n/types'
import { useT } from '@/i18n/use-t'
import { usesMockAPI } from '@/lib/api/backend-adapters'
import { cn } from '@/lib/cn'
import { useLocale } from '@/lib/queries'
import { useLogout, useSession } from '@/lib/session'

type TabTo = '/$locale/today' | '/$locale/discover' | '/$locale/search' | '/$locale/library' | '/$locale/me'

interface Tab {
  key: MessageKey
  to: TabTo
  match: string[]
  icon: ComponentType<{ size?: number }>
  desktop: boolean
}

const TABS: Tab[] = [
  { key: 'nav.today', to: '/$locale/today', match: ['today', 'videos'], icon: HomeIcon, desktop: true },
  {
    key: 'nav.discover',
    to: '/$locale/discover',
    match: ['discover', 'actresses'],
    icon: CompassIcon,
    desktop: true,
  },
  { key: 'nav.search', to: '/$locale/search', match: ['search'], icon: SearchIcon, desktop: false },
  { key: 'nav.library', to: '/$locale/library', match: ['library'], icon: LibraryIcon, desktop: true },
  { key: 'nav.me', to: '/$locale/me', match: ['me'], icon: UserIcon, desktop: true },
]

function useActiveSection(): string {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  return pathname.split('/')[2] ?? ''
}

export function Logo({ withWord = true }: { withWord?: boolean }) {
  const t = useT()
  return (
    <span className="flex items-center gap-3 text-moon">
      <img src="/brand/icon-96.png" alt="" width={34} height={34} className="size-[34px] rounded-[10px]" />
      {withWord ? (
        <span className="font-display text-[17px] font-bold tracking-tight whitespace-nowrap">
          {t('brand.name')}
        </span>
      ) : null}
    </span>
  )
}

function AccountMenu() {
  const t = useT()
  const locale = useLocale()
  const navigate = useNavigate()
  const { data: user } = useSession()
  const logout = useLogout()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  if (!user) return null
  const switchLocale = (next: string) => {
    const rest = pathname.split('/').slice(2).join('/')
    void navigate({ to: `/${next}/${rest}` as '/', search: true, hash: true })
  }
  const item =
    'flex h-10 w-full cursor-default items-center gap-2.5 rounded-lg px-3 text-sm text-moon outline-none data-[highlighted]:bg-dusk'
  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label={t('nav.account')}
        className="flex size-10 items-center justify-center rounded-full bg-[#2B2340] text-sm font-bold text-violet-soft hover:ring-2 hover:ring-line-strong"
      >
        {user.email.slice(0, 1).toUpperCase()}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={8} align="end" className="z-50">
          <Menu.Popup className="min-w-56 rounded-[14px] border border-line bg-night p-1.5 shadow-[0_16px_48px_rgba(0,0,0,0.5)] outline-none">
            <div className="px-3 py-2 text-xs text-faint">{user.email}</div>
            <Menu.Item
              className={item}
              onClick={() =>
                void navigate({ to: '/$locale/me', params: { locale }, search: { section: 'overview' } })
              }
            >
              <UserIcon size={16} />
              {t('nav.me')}
            </Menu.Item>
            {user.role === 'ADMIN' ? (
              <Menu.Item
                className={item}
                onClick={() => void navigate({ to: '/$locale/admin', params: { locale } })}
              >
                <EarMark size={16} />
                {t('nav.admin')}
              </Menu.Item>
            ) : null}
            <Menu.Separator className="my-1 h-px bg-line" />
            <div className="flex items-center gap-2 px-3 pt-1.5 pb-1 text-xs text-faint">
              <GlobeIcon size={14} />
              {t('nav.language')}
            </div>
            <Menu.RadioGroup value={locale} onValueChange={(value) => switchLocale(String(value))}>
              {LOCALES.map((l) => (
                <Menu.RadioItem key={l} value={l} className={cn(item, 'h-9 pl-8')}>
                  <Menu.RadioItemIndicator className="absolute left-3 text-magenta">
                    •
                  </Menu.RadioItemIndicator>
                  {LOCALE_LABELS[l]}
                </Menu.RadioItem>
              ))}
            </Menu.RadioGroup>
            <Menu.Separator className="my-1 h-px bg-line" />
            <Menu.Item
              className={item}
              onClick={() =>
                logout.mutate(undefined, {
                  onSettled: () =>
                    void navigate({
                      to: '/$locale/login',
                      params: { locale },
                      search: { tab: 'login', redirect: '' },
                    }),
                })
              }
            >
              <LogoutIcon size={16} />
              {t('nav.logout')}
            </Menu.Item>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  )
}

function TopBar({ onSearch }: { onSearch: () => void }) {
  const t = useT()
  const locale = useLocale()
  const active = useActiveSection()
  const { data: user } = useSession()
  return (
    <header className="sticky top-0 z-40 hidden h-[72px] items-center gap-8 border-b border-line-soft bg-ink/90 px-6 backdrop-blur lg:flex xl:px-12">
      <Link to="/$locale/today" params={{ locale }} aria-label={t('brand.name')}>
        <Logo />
      </Link>
      <nav className="ml-2 flex gap-7" aria-label={t('ui.mainNavigation')}>
        {TABS.filter((tab) => tab.desktop).map((tab) => {
          const on = tab.match.includes(active)
          return (
            <Link
              key={tab.to}
              to={tab.to}
              params={{ locale }}
              aria-current={on ? 'page' : undefined}
              className={cn(
                'flex flex-col items-center gap-0.5 text-[15px] font-semibold whitespace-nowrap',
                on ? 'text-moon' : 'text-faint hover:text-moon',
              )}
            >
              <EarMark active={on} />
              {t(tab.key)}
            </Link>
          )
        })}
      </nav>
      <button
        type="button"
        onClick={onSearch}
        className="ml-auto flex h-[42px] w-full max-w-[380px] min-w-0 items-center gap-2.5 rounded-xl bg-dusk px-3.5 text-left text-sm text-faint hover:bg-hover"
      >
        <SearchIcon size={16} />
        <span className="flex-1">{t('nav.searchPlaceholder')}</span>
        <kbd className="rounded-[5px] border border-line-strong px-1.5 py-0.5 font-mono text-xs text-haze">
          ⌘K
        </kbd>
      </button>
      {user?.telegram.bound ? (
        <span className="hidden items-center gap-2 rounded-full bg-cyan-bg px-3 py-1.5 text-[13px] whitespace-nowrap text-cyan-text xl:flex">
          <span className="size-2 rounded-full bg-cyan" />
          Telegram
        </span>
      ) : null}
      <AccountMenu />
    </header>
  )
}

function MobileTabBar() {
  const t = useT()
  const locale = useLocale()
  const active = useActiveSection()
  return (
    <nav
      aria-label={t('ui.mainNavigation')}
      className="pb-safe fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-line-soft bg-bar/95 backdrop-blur lg:hidden"
    >
      {TABS.map((tab) => {
        const on = tab.match.includes(active)
        const Icon = tab.icon
        return (
          <Link
            key={tab.to}
            to={tab.to}
            params={{ locale }}
            aria-current={on ? 'page' : undefined}
            onClick={() => {
              if (on) window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
            className={cn(
              'flex h-[62px] flex-col items-center justify-center gap-0.5 text-[11px]',
              on ? 'text-moon' : 'text-faint',
            )}
          >
            <EarMark active={on} size={16} />
            <Icon size={22} />
            {t(tab.key)}
          </Link>
        )
      })}
    </nav>
  )
}

export function MockBanner() {
  const t = useT()
  if (!usesMockAPI) return null
  return (
    <div className="border-b border-line-soft bg-deep px-4 py-1.5 text-center text-[11px] text-faint">
      {t('common.mockNotice')}
    </div>
  )
}

export function AppShell() {
  const t = useT()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const active = useActiveSection()
  const hideTabBar = active === 'videos'

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((open) => !open)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="min-h-dvh">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:bg-magenta focus:px-3 focus:py-2 focus:text-ink"
      >
        {t('nav.skipToContent')}
      </a>
      <MockBanner />
      <TopBar onSearch={() => setPaletteOpen(true)} />
      <main
        id="main"
        className={cn('mx-auto w-full max-w-[1440px]', hideTabBar ? 'pb-28 lg:pb-12' : 'pb-28 lg:pb-12')}
      >
        <Outlet />
      </main>
      {hideTabBar ? null : <MobileTabBar />}
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  )
}
